import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { ENV } from '../../../config/env';
import toast from 'react-hot-toast';

interface TitleVariation {
  title: string;
  score: number;
  keywords: string[];
  charCount: number;
  reason: string;
}

const ProductTitleTab: React.FC = () => {
  const { user, getToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [productName, setProductName] = useState('');
  const [category, setCategory] = useState('');
  const [keyFeatures, setKeyFeatures] = useState('');
  const [platform, setPlatform] = useState('general');
  const [variations, setVariations] = useState<TitleVariation[]>([]);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!productName.trim() || !category.trim()) {
      toast.error('Please enter product name and category');
      return;
    }

    try {
      setLoading(true);
      toast.loading('Generating title variations...');

      const userId = await getToken();
      const response = await fetch(`${ENV.API_URL}/api/marketing/product-title`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, productName, category, keyFeatures, platform })
      });

      if (!response.ok) throw new Error('Failed to generate titles');

      const data = await response.json();
      toast.dismiss();
      setVariations(data.variations);
      toast.success(`Generated ${data.variations.length} title variations!`);
    } catch (error) {
      toast.dismiss();
      console.error('Error:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to generate titles');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 85) return 'bg-green-100 text-green-800';
    if (score >= 70) return 'bg-blue-100 text-blue-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Product Title Optimizer</h3>
        <p className="text-gray-600 text-sm">
          Generate SEO-optimized product titles for Amazon, Shopify, eBay, and other platforms.
        </p>
      </div>

      <form onSubmit={handleGenerate} className="mb-8 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Product Name
            </label>
            <input
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="e.g., Wireless Bluetooth Headphones"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g., Electronics > Audio"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Key Features (Optional)
          </label>
          <input
            type="text"
            value={keyFeatures}
            onChange={(e) => setKeyFeatures(e.target.value)}
            placeholder="e.g., Noise Cancelling, 30hr Battery, Premium Sound"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Platform
          </label>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            <option value="general">General (All Platforms)</option>
            <option value="amazon">Amazon (200 chars)</option>
            <option value="shopify">Shopify (70 chars)</option>
            <option value="ebay">eBay (80 chars)</option>
            <option value="etsy">Etsy (140 chars)</option>
          </select>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`px-6 py-3 rounded-lg font-medium text-white transition ${
            loading ? 'bg-orange-400 cursor-not-allowed' : 'bg-orange-600 hover:bg-orange-700'
          }`}
        >
          {loading ? 'Generating...' : 'Generate Titles'}
        </button>
      </form>

      {variations.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-gray-900">
            {variations.length} Optimized Title Variations
          </h4>

          {variations.map((variation, index) => (
            <div key={index} className="bg-white rounded-lg border border-gray-200 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h5 className="text-lg font-semibold text-gray-900 mb-2">{variation.title}</h5>
                  <p className="text-sm text-gray-600">{variation.reason}</p>
                </div>
                <div className="ml-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(variation.score)}`}>
                    Score: {variation.score}
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  {variation.charCount} characters
                </span>
                <span className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  {variation.keywords.length} keywords
                </span>
                <div className="flex flex-wrap gap-2">
                  {variation.keywords.map((keyword, i) => (
                    <span key={i} className="px-2 py-1 bg-orange-50 text-orange-700 rounded text-xs">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(variation.title);
                    toast.success('Title copied!');
                  }}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm font-medium"
                >
                  Copy Title
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && variations.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No titles generated yet</h3>
          <p className="mt-1 text-sm text-gray-500">Enter product details above to get started</p>
        </div>
      )}
    </div>
  );
};

export default ProductTitleTab;
